import React, { useMemo, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Toolbar,
  Divider,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Receipt,
  Payment,
  ShoppingCart,
  Contacts,
  ExpandMore,
  Store,
  Home,
  TableView,
  AdminPanelSettings,
  PushPin,
  PushPinOutlined,
  AccountBalanceWallet,
  LocalOffer,
  Assessment,
} from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MobileDrawer from "../MobileDrawer";
import { useDispatch } from "react-redux";
import { setActiveTabByPath } from "../../tabs/tabsSlice";
import { useAuth } from "../../auth/AuthProvider";
import { hasPerm } from "../../auth/permissions";

// Registro de secciones con permisos por sección/subitems
const PAGES = [
  {
    text: "Proveedores",
    icon: <Contacts />,
    permSection: "view_supplier",
    subitems: [
      { text: "Nuevo Proveedor", link: "/suppliers/create-new", required: "manage_supplier" },
      { text: "Consulta de proveedores", link: "/suppliers/all-suppliers", required: "view_supplier" },
      { text: "Consulta de Ejecutivos", link: "/suppliers/executive/all-executives", required: "view_executive" },
    ],
  },
  {
    text: "Productos",
    icon: <Store />,
    permSection: "view_product",
    subitems: [
      { text: "Nuevo Producto", link: "/orders/product/create-new", required: "manage_product" },
      { text: "Consulta de Productos", link: "/orders/product/all-products", required: "view_product" },
    ],
  },
  {
    text: "Órdenes",
    icon: <ShoppingCart />,
    permSection: "view_order",
    subitems: [
      { text: "Generar Nueva Orden", link: "/orders/create-new", required: "create_order" },
      { text: "Consulta de Ordenes", link: "/orders/all-orders", required: "view_order" },
    ],
  },
  {
    text: "Facturas",
    icon: <Receipt />,
    permSection: "view_invoice",
    subitems: [
      { text: "Ingresar Facturas", link: "/invoice/create-new", required: "create_invoice" },
      { text: "Confirmar Recepcion", link: "/invoice/confirm", required: "confirm_invoice" },
      { text: "Consulta de Facturas", link: "/invoice/all-invoices", required: "view_invoice" },
      { text: "Kardex recepciones", link: "/orders/kardex/recepciones", required: "view_invoice" },
      { text: "Kardex homogeneo", link: "/orders/kardex/homogeneo", required: "view_invoice" },
    ],
  },
  {
    text: "Reportes",
    icon: <Assessment />,
    permSection: "view_invoice",
    subitems: [
      { text: "Obligaciones historicas", link: "/invoice/reports/provider-status", required: "view_invoice" },
    ],
  },
  {
    text: "Promociones (Beta)",
    icon: <LocalOffer />,
    permSection: "view_sellout",
    subitems: [
      { text: "Promociones Sell Out", link: "/sellout/promotions", required: "view_sellout" },
      { text: "Faltantes Sell Out", link: "/sellout/missing", required: "view_sellout" },
      { text: "Ingreso documental", link: "/sellout/documents", required: "view_sellout" },
    ],
  },
  {
    text: "Pagos",
    icon: <Payment />,
    permSection: "view_payment",
    subitems: [
      { text: "Pagar Factura", link: "/payment/make-payment", required: "settle_payment" },
      { text: "Notas de credito", link: "/payment/credit-notes", required: "settle_payment" },
      { text: "Recibos de caja", link: "/payment/receipts", required: "settle_payment" },
      { text: "Transacciones", link: "/payments", required: "view_payment" },
      { text: "Abasto", link: "/abasto", required: "settle_payment" },
    ],
  },
  {
    text: "Deudas",
    icon: <AccountBalanceWallet />,
    permSection: "settle_payment",
    subitems: [
      { text: "Lista de deudas", link: "/debts/", required: "settle_payment" },
    ],
  },
  {
    text: "Catálogos",
    icon: <TableView />,
    permSection: "view_catalogs",
    subitems: [
      { text: "Bodegas", link: "/catalogs/warehouses", required: "view_catalogs" },
      { text: "Formatos", link: "/catalogs/formats", required: "view_catalogs" },
      { text: "Categorías", link: "/catalogs/categories", required: "view_catalogs" },
      { text: "Tipos de precio", link: "/catalogs/price-types", required: "view_catalogs" },
      { text: "Tipos de objetivo", link: "/catalogs/objective-types", required: "view_catalogs" },
      { text: "Tipos de factura", link: "/catalogs/invoice-types", required: "view_catalogs" },
      { text: "Notas de entrada", link: "/catalogs/receive-notes", required: "view_catalogs" },
    ],
  },
];

function SlideBar({ pinned, setPinned, drawerOpen, setDrawerOpen }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const dispatch = useDispatch();
  const { permissions, isSuperuser, loading, authVersion, company, isAuthenticated } = useAuth();

  const companyInitials = useMemo(() => {
    const name = (company && company.name) ? String(company.name) : 'STX';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'STX';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }, [company]);

  // ADMIN
  const adminSections = useMemo(() => {
    const showAdmin =
      isSuperuser || hasPerm(permissions, "manage_users") || hasPerm(permissions, "manage_roles");
    if (!showAdmin) return [];

    if (isSuperuser) {
      return [
        {
          text: "Administración",
          icon: <AdminPanelSettings />,
          permSection: "admin",
          subitems: [
            { text: "Empresas", link: "/admin/companies" },
            { text: "Usuarios", link: "/admin/users" },
            { text: "Permisos", link: "/admin/permissions" },
            { text: "Roles", link: "/admin/roles" },
          ],
        },
      ];
    }

    const sub = [];
    if (hasPerm(permissions, "view_users")) sub.push({ text: "Usuarios", link: "/admin/users" });
    if (hasPerm(permissions, "manage_roles")) sub.push({ text: "Roles", link: "/admin/roles" });
    // if (hasPerm(permissions, "manage_users"))
    //   sub.push({ text: "Asignaciones", link: "/admin/user-assignments" });

    if (sub.length === 0) return [];
    return [{ text: "Administración", icon: <AdminPanelSettings />, permSection: "admin", subitems: sub }];
  }, [permissions, isSuperuser]);

  // BUSINESS
  const businessSections = useMemo(() => {
    if (!Array.isArray(PAGES)) return [];

    const secs = PAGES.map((sec) => {
      const visibleSubitems = (sec.subitems || []).filter((si) =>
        hasPerm(permissions, si.required)
      );

      // Mostrar la sección si:
      // - tiene el permiso de sección, O
      // - tiene al menos un sub-permiso (por ejemplo, solo create_invoice)
      const canSeeSection =
        hasPerm(permissions, sec.permSection) || visibleSubitems.length > 0;

      if (!canSeeSection) return null;

      return {
        ...sec,
        subitems: visibleSubitems,
      };
    }).filter(Boolean);

    return secs;
  }, [permissions]);

  const sections = useMemo(() => {
    if (isSuperuser) {
      const hasBusiness = Array.isArray(permissions) && permissions.some((p) => p !== "__superuser__");
      return hasBusiness ? [...adminSections, ...businessSections] : adminSections;
    }
    if (!permissions || permissions.length === 0) return [];
    return [...adminSections, ...businessSections];
  }, [adminSections, businessSections, isSuperuser, permissions]);

  const toggleDrawer = () => {
    setPinned(!pinned);
    setOpen(!pinned);
  };

  // Al cerrar sesión: colapsar y limpiar estados para evitar que quede fijado/trabado
  React.useEffect(() => {
    if (!isAuthenticated) {
      setOpen(false);
      setExpanded(null);
      setPinned(false);
    } else if (!loading) {
      setOpen(Boolean(pinned)); // asegurar que al reingresar y estar pineado se muestre el texto
    }
  }, [isAuthenticated, loading, pinned, setPinned]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const handleNavigate = (ruta) => navigate(ruta);

  const handleHome = () => {
    navigate("/home");
    dispatch(setActiveTabByPath(null));
  };

  if (loading) {
    return null;
  }

  return (
    <>
      {!isMobile && (
        <Box
          key={authVersion}
          sx={{
            position: "fixed",
            top: 70,
            left: 10,
            zIndex: 1200,
          }}
          onMouseEnter={() => !pinned && setOpen(true)}
          onMouseLeave={() => !pinned && setOpen(false)}
        >
          <Drawer
            variant="permanent"
            sx={{
              width: pinned ? 240 : open ? 235 : 60,
              transition: "width 0.35s ease",
              overflowX: "hidden",
              background: (theme) => theme.palette.secondary.main,
              borderRadius: "8px",
              "& .MuiDrawer-paper": {
                width: pinned ? 240 : open ? 235 : 80,
                transition: "width 0.35s ease",
                overflow: "hidden", // el scroll vive en el contenedor interno
                height: "calc(100vh - 80px)",
                position: "relative",
                background: (theme) => theme.palette.secondary.main,
                color: "white",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              },
            }}
          >
            {/* HEADER + SCROLL AREA */}
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
              <Toolbar sx={{ width: "100%", display: "flex", justifyContent: "center", right: "35px" }}>
                <IconButton onClick={toggleDrawer} sx={{ color: "white" }}>
                  {pinned ? <Tooltip title='Colapsar'><PushPin /></Tooltip> : <Tooltip title='Expandir'><PushPinOutlined /></Tooltip>}
                </IconButton>
              </Toolbar>
              <Divider sx={{ width: "100%", opacity: 0.25 }} />

              {/* Área desplazable con scroll invisible y sin empujar el layout */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  scrollbarWidth: "none",          // Firefox: oculta scroll
                  msOverflowStyle: "none",         // IE/Edge: oculta scroll
                  "&::-webkit-scrollbar": {        // Chrome/Safari: oculta scroll
                    width: 0,
                    height: 0,
                  },
                  scrollbarGutter: "stable both-edges", // evita salto de layout
                  pr: 0.5,
                }}
              >
                <List sx={{ width: "100%", p: 0, background: "transparent" }}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={handleHome}
                      sx={{
                        color: "white",
                        ":hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                        width: open ? "100%" : "60px",
                        transition: "width 0.25s ease",
                        borderRadius: 1.5,
                        mx: open ? 1 : 0.5,
                        my: 0.5,
                        right: '3px'
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "auto", color: "white", mb: 0.5 }}>
                        <Home />
                      </ListItemIcon>
                      {open && (
                        <ListItemText primary="Inicio" sx={{ whiteSpace: "nowrap", pl: "10px" }} />
                      )}
                    </ListItemButton>
                  </ListItem>

                  {sections.map(({ text, icon, subitems }, index) => (
                    <Accordion
                      key={text}
                      expanded={expanded === index && open}
                      onChange={handleAccordionChange(index)}
                      sx={{
                        background: "transparent",
                        color: "white",
                        boxShadow: "none",
                        "&.Mui-expanded": { margin: 0 },
                        "&:before": { display: "none" },
                        width: open ? "100%" : "60px",
                        transition: "width 0.25s ease",
                        borderRadius: 1.5,
                        mx: open ? 1 : 0.5,
                        right: '3px',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={open && subitems ? <ExpandMore sx={{ color: "white" }} /> : null}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: 46,
                          px: 2,
                          justifyContent: open ? "flex-start" : "center",
                          borderRadius: 1.5,
                          ":hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: "auto",
                            color: "white",
                            textAlign: open ? "left" : "center",
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={text}
                          sx={{
                            whiteSpace: "nowrap",
                            display: open ? "block" : "none",
                            pl: "10px",
                          }}
                        />
                      </AccordionSummary>

                      {subitems && open && (
                        <AccordionDetails sx={{ p: 0, pb: 0.5 }}>
                          <List sx={{ py: 0.5 }}>
                            {subitems.map((subItem) => (
                              <ListItem key={subItem.text} sx={{ pl: 1 }}>
                                <ListItemButton
                                  onClick={() => handleNavigate(subItem.link)}
                                  sx={{
                                    color: "white",
                                    borderRadius: 1.5,
                                    py: 0.75,
                                    px: 1.5,
                                    ":hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                                  }}
                                >
                                  {subItem.text}
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      )}
                    </Accordion>
                  ))}
                </List>
              </Box>
            </Box>

            {/* FOOTER CORPORATIVO (centrado perfecto) */}
            <Box
              sx={{
                height: 58,
                px: open ? 1.25 : 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                background: (theme) =>
                  `linear-gradient(180deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                borderTop: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "inset 0 1px 0 rgba(0,0,0,0.2)",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
                position: "relative",
              }}
            >
              {/* Colapsado: monograma centrado absoluto */}
              {!open && (
                <Tooltip title={`${company?.name || 'STX'} © ${new Date().getFullYear()}`} placement="right">
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      letterSpacing: 0.5,
                      background: "rgba(255,255,255,0.15)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      backdropFilter: "blur(4px)",
                      userSelect: "none",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-75%, -50%)", // centrado perfecto
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "white" }}>{companyInitials}</Typography>
                  </Box>
                </Tooltip>
              )}

              {/* Expandido: marca + año */}
              {open && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 600,
                    letterSpacing: "0.6px",
                    transition: "opacity .2s ease",
                    userSelect: "none",
                  }}
                >
                  {(company && company.name) ? company.name : 'STX'} © {new Date().getFullYear()}
                </Typography>
              )}
            </Box>
          </Drawer>
        </Box>
      )}

      {isMobile && (
        <MobileDrawer
          pages={sections}
          handleNavigate={handleNavigate}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
        />
      )}
    </>
  );
}

export default SlideBar;
